import { API_BASE } from '../../../config.js';
// SMART CONTENT CLIENT (Smart Tools epic · SM-H, 2026-07-23) — the authoring-
// time bridge from the editor's smart tools to the IIG-backed curriculum
// content service (the be_printables Cloudflare worker + the knowledge graph).
//
// LAWS (owner + epic charter):
//   • GRAPH PROPOSES, TEACHER DISPOSES — this module only FETCHES; the modal
//     drops results into the DRAFT for the educator to review/edit.
//   • ids-never-URLs — payloads carry opaque graph ids, never links.
//   • no student PII on this wire.
//   • EXPORTABILITY — the network is touched at AUTHORING time only. Nothing
//     here runs at render/export; recipes render from plain-JSON params alone.
//
// QUIET-FAILURE CONTRACT: every call resolves with typed data or THROWS a
// tagged error `{ unavailable: true }` on any network fault / timeout / 404 /
// non-success envelope. Callers catch it and stay silent — the standards
// service being down must NEVER break manual editing. No API keys live here;
// these routes are public read-only (standard codes aren't sensitive).
//
// Endpoints (pse-printables worker; envelope `{ success, data }`):
//   GET /api/states                         → { states:  [{ code, name, setCount }] }
//   GET /api/states/:code/subjects          → { subjects:[{ subject, setCount }] }
//   GET /api/states/:code/standards?subject=&q=&limit=
//                                           → { standards:[{ id, code, description,
//                                               gradeMin, gradeMax, subject, setTitle }], … }
//   GET /api/standards/:id/content          → StandardContentPayload (SM-H route)

// Brightivity: the printables fork behind the front door (brightivity.dev/printables/<x> = /api/<x>
// in the StudioCore router), so every '/api/…' path below is sent as '/printables/…'.
function baseUrl() {
  if (typeof window !== 'undefined' && window.__PSE_CONTENT_API__) {
    return String(window.__PSE_CONTENT_API__).replace(/\/+$/, '');
  }
  return `${API_BASE}/printables`;
}
const routed = (path) => path.replace(/^\/api\//, '/');

/** The one error shape callers key on — anything that means "no service". */
function unavailable(reason) {
  const err = new Error('Standards content service is unavailable');
  err.unavailable = true;
  if (reason != null) err.reason = String(reason);
  return err;
}

// Two-letter state code guard (worker route is /:code([A-Za-z]{2})). Keeps a
// malformed pick from ever leaving the client as a doomed request.
function normState(code) {
  const c = String(code || '').trim().toUpperCase();
  return /^[A-Z]{2}$/.test(c) ? c : null;
}

/**
 * GET `path`, unwrap the `{ success, data }` envelope, return `data`.
 * Aborts after `timeoutMs`. Throws the tagged `unavailable` error on ANY
 * failure (network, timeout, 404, !ok, or success:false).
 */
async function apiGet(path, { timeoutMs = 8000 } = {}) {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  let res;
  try {
    res = await fetch(`${baseUrl()}${routed(path)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: ctrl ? ctrl.signal : undefined,
    });
  } catch (e) {
    throw unavailable(e && e.message); // network error / DNS / abort / CSP
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (res.status === 404) throw unavailable('404');
  let json;
  try {
    json = await res.json();
  } catch (e) {
    throw unavailable('bad-json');
  }
  if (!res.ok || !json || json.success !== true) {
    throw unavailable((json && json.error) || `http-${res.status}`);
  }
  return json.data;
}

/**
 * POST `body` as JSON to `path`, unwrap the `{ success, data }` envelope, return
 * `data`. The write-side mirror of apiGet (apiGet is GET-only): same abort
 * budget, same tagged-`unavailable` contract on ANY failure (network, timeout,
 * 404, !ok, success:false). Used by the stateless intent-customization route —
 * no keys, no PII; the worker is the only thing that talks to the model.
 */
async function apiPost(path, body, { timeoutMs = 22000 } = {}) {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
  let res;
  try {
    res = await fetch(`${baseUrl()}${routed(path)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {}),
      signal: ctrl ? ctrl.signal : undefined,
    });
  } catch (e) {
    throw unavailable(e && e.message); // network error / DNS / abort / CSP
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (res.status === 404) throw unavailable('404');
  let json;
  try {
    json = await res.json();
  } catch (e) {
    throw unavailable('bad-json');
  }
  if (!res.ok || !json || json.success !== true) {
    throw unavailable((json && json.error) || `http-${res.status}`);
  }
  return json.data;
}

// ── availability probe: ONE cached GET /api/states (5s) shared with fetchStates
// so opening the modal costs a single request. A positive result sticks for the
// session; a failure drops the cache so a later open can retry a transient blip.
let _statesCache = null;
function loadStates() {
  if (!_statesCache) {
    _statesCache = apiGet('/api/states', { timeoutMs: 5000 })
      .then((data) => {
        const rows = data && Array.isArray(data.states) ? data.states : [];
        // Never cache an EMPTY result (VA audit): a transient success:true with
        // 0 states would stick forever → availability true but list empty →
        // modal bricks until reload. Throw so the cache resets and a re-probe
        // (Try again / re-expand) can recover.
        if (!rows.length) { _statesCache = null; throw unavailable('empty-states'); }
        return rows;
      });
    _statesCache.catch(() => { _statesCache = null; });
  }
  return _statesCache;
}

/**
 * Resolve to `true` when the standards service answers, `false` otherwise.
 * NEVER throws — the modal uses it to decide between the fill block and a
 * single muted "service isn't reachable" line.
 */
export function contentApiAvailable() {
  return loadStates().then(() => true, () => false);
}

/** States list: [{ code, name, setCount }]. Throws `unavailable` on failure. */
export function fetchStates() {
  return loadStates();
}

/** Subjects for a state: [{ subject, setCount }]. Throws `unavailable`. */
export async function fetchSubjects(state) {
  const code = normState(state);
  if (!code) throw unavailable('bad-state');
  const data = await apiGet(`/api/states/${code}/subjects`);
  return data && Array.isArray(data.subjects) ? data.subjects : [];
}

/**
 * Search standards in a state (optionally narrowed by subject).
 * Returns the standards array: [{ id, code, description, gradeMin, gradeMax,
 * subject, setTitle }]. `limit` defaults to 8 (the modal shows at most 8).
 * Throws `unavailable` on failure.
 */
export async function searchStandards({ state, subject, q, limit = 8 } = {}) {
  const code = normState(state);
  if (!code) throw unavailable('bad-state');
  const params = new URLSearchParams();
  if (subject) params.set('subject', subject);
  const query = (q || '').trim();
  if (query) {
    params.set('q', query);
    // Opt-in word-prefix matching ("pu" → punctuation/purpose while typing).
    // The product's own picker never sends this flag, so its behavior is
    // untouched — core-product-safety law, owner 2026-07-23.
    params.set('prefix', '1');
  }
  params.set('limit', String(limit));
  const qs = params.toString();
  const data = await apiGet(`/api/states/${code}/standards${qs ? `?${qs}` : ''}`);
  return data && Array.isArray(data.standards) ? data.standards : [];
}

/**
 * Fetch the assembled StandardContentPayload for a standard id (the SM-H
 * route). The graph's PROPOSAL — the caller runs recipe.contentSource
 * .applyContent on it and merges the result into the draft. Throws
 * `unavailable` on network/404 (unknown standard) — the caller stays quiet.
 *
 * Returns the `data` envelope verbatim, so the SM-J provenance fields pass
 * through untouched: `generated` (true ⇒ AI-drafted, not teacher-reviewed) and
 * `enrichment` ('queued' | 'unavailable', present only when nothing was served).
 * The modal polls this same call while `enrichment === 'queued'`.
 */
export async function fetchStandardContent(id, want) {
  const sid = String(id || '').trim();
  if (!sid) throw unavailable('bad-id');
  // `want` = the recipe's contentSource.wants. The worker drafts the buckets
  // the TOOL needs when the graph lacks them (even if it has other content) —
  // without it, drafting only fires when the graph is entirely empty.
  const wants = Array.isArray(want) ? want.filter(Boolean) : [];
  const qs = wants.length ? `?want=${encodeURIComponent(wants.join(','))}` : '';
  // 40s: worst case is two 18s model calls (thin-vocab retry, or a graph
  // thin-topup draft) ≈ 36s — the client budget must clear the server's own
  // timeout with margin so it never clips a fill the server completes+bills.
  // States/subjects/search keep the snappy default.
  return apiGet(`/api/standards/${encodeURIComponent(sid)}/content${qs}`, { timeoutMs: 40000 });
}

// ── INTENT CUSTOMIZATION (Smart Tools · lane IX, 2026-07-24) — the authoring-
// time bridge for the modal's "✨ Adjust with AI" box. The teacher types an
// intent; the worker translates it into a SCHEMA-VALIDATED param patch (only
// keys that map to a real field of THIS tool, values coerced/clamped to type).
// Same laws as Smart Fill: the model PROPOSES into the DRAFT, the teacher
// disposes; nothing reaches the canvas until Insert/Apply; quiet-failing so a
// gateway blip never blocks manual editing. This composes WITH Smart Fill —
// fill pulls graph CONTENT, intent adjusts VALUES/styling — over the same draft.

/**
 * Resolve to `true` when the customize route is armed and reachable, `false`
 * otherwise. NEVER throws. A cheap `{probe:true}` POST (no model call, no spend)
 * — the modal uses it to decide whether to render the "Adjust with AI" box at
 * all. Cached for the session on success; a negative/failed probe drops the
 * cache so a later open re-probes (covers the owner arming the flag mid-session).
 */
let _customizeProbe = null;
export function customizeAvailable() {
  if (!_customizeProbe) {
    _customizeProbe = apiPost('/api/tools/customize', { probe: true }, { timeoutMs: 6000 })
      .then((data) => {
        const ok = !!(data && data.available === true);
        if (!ok) _customizeProbe = null; // never cache a negative — allow re-probe
        return ok;
      })
      .catch(() => { _customizeProbe = null; return false; });
  }
  return _customizeProbe;
}

/**
 * Adjust a tool's settings from a natural-language intent. POST the tool id, its
 * EDITABLE FIELDS (key/type/label/options/min/max — built from recipe.schema,
 * advanced/provenance fields excluded), the CURRENT draft params, and the
 * teacher's intent. Returns `{ patch, note, generated }` — `patch` is the
 * worker's schema-validated set of changed keys (already coerced to each field's
 * type; may be empty when nothing mapped), `note` a one-line human summary.
 * Throws the tagged `unavailable` error on any fault / when the route is off.
 */
export async function customizeTool({ toolId, fields, params, intent } = {}) {
  const text = String(intent || '').trim();
  if (!text) throw unavailable('empty-intent');
  const data = await apiPost('/api/tools/customize', {
    toolId: String(toolId || ''),
    fields: Array.isArray(fields) ? fields : [],
    params: params && typeof params === 'object' ? params : {},
    intent: text,
  }, { timeoutMs: 22000 }); // clears the worker's 18s model timeout with margin
  return {
    patch: data && data.patch && typeof data.patch === 'object' ? data.patch : {},
    note: data && typeof data.note === 'string' ? data.note : '',
    generated: !!(data && data.generated),
  };
}
